import { Link } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { MenuOption, MenuOptionIcon, type MenuOptionProps } from './menu-option';
import { Skeleton } from './skeleton';

type NavItemProps = {
  label: string;
  icon?: ReactNode;
  secondaryIcon?: ReactNode;
  href?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  className?: string;
  badge?: ReactNode;
  asLink?: boolean;
  onClick?: () => unknown;
  isLoading?: boolean;
  search?: Record<string, string>;
  variant?: MenuOptionProps['variant'];
  target?: string;
  params?: Record<string, string>;
};

export function NavItem(props: NavItemProps) {
  const {
    label,
    icon,
    href = '',
    isActive,
    className = '',
    isDisabled = false,
    badge,
    asLink = true,
    secondaryIcon,
    onClick,
    isLoading = false,
    variant = 'gray',
    search,
    target = '_self',
    params,
  } = props;
  const classNames = twMerge([
    'grid w-full grid-cols-[auto_1fr_auto] items-center gap-2 rounded-md px-3 py-2 text-left cursor-pointer bg-transparent hover:bg-lavander-100 hover:text-lavander-600 group h-9',
    isActive
      ? 'bg-lavander-100 text-lavander-600 cursor-default hover:bg-lavander-100 pointer-events-none'
      : 'text-black',
    isDisabled ? 'cursor-default hover:bg-transparent hover:text-black text-black' : '',
    className,
  ]);

  const styledItem = (
    <MenuOption
      variant={variant}
      className={classNames}
      leftElement={
        <MenuOptionIcon disabled={isDisabled} isActive={isActive} variant={variant}>
          {icon}
        </MenuOptionIcon>
      }
      mainText={label}
      isActive={isActive}
      disabled={isDisabled}
      rightElement={
        <div className="flex items-center justify-between gap-2">
          {badge != null ? badge : null}
          {secondaryIcon != null ? (
            <MenuOptionIcon disabled={isDisabled} isActive={isActive} variant={variant}>
              {secondaryIcon}
            </MenuOptionIcon>
          ) : null}
        </div>
      }
    />
  );

  // TODO: this should be set on the link but with a prevent default
  if (onClick != null) {
    return (
      <button type="button" onClick={onClick} disabled={isDisabled} className="w-full">
        {styledItem}
      </button>
    );
  }

  if (isLoading) {
    return (
      <div className={classNames}>
        <Skeleton className="size-4" />
        <Skeleton className="h-4 w-[50%]" />
      </div>
    );
  }

  if (!asLink || isActive) {
    return styledItem;
  }

  return (
    <Link to={href} search={search} target={target} disabled={isDisabled} params={params}>
      {styledItem}
    </Link>
  );
}
